import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const SearchBox = ({
    searchResults = [],
    searchBox,
    setSearchBox,
    loading = false,
    minChars = 2,
    maxItems = 10
}) => {
    const [focused, setFocused] = useState(false);
    const navigate = useNavigate();

    const trimmedQuery = (searchBox || '').trim();
    const showDropdown = focused && trimmedQuery.length >= minChars;
    const isResultsValid = Array.isArray(searchResults) && searchResults.length > 0;

    const visibleResults = useMemo(() => {
        if (!Array.isArray(searchResults)) return [];
        const limit = Math.max(1, Number(maxItems) || 10);
        return searchResults.slice(0, limit);
    }, [searchResults, maxItems]);

    const hasMoreResults =
        isResultsValid &&
        Array.isArray(searchResults) &&
        visibleResults.length < searchResults.length;

    const canShowEmptyState = useMemo(() => {
        // Show "no results" only after we have a real query and we're not loading.
        return showDropdown && !loading;
    }, [showDropdown, loading]);

    return (
        <div className="relative w-full flex justify-end ">
            <input
                id="search-input"
                type="text"
                value={searchBox}
                onChange={(e) => setSearchBox(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder="Kërko produkte"
                className={`
                    transition-all duration-500 ease-in-out
                    px-5 py-3 rounded shadow-md border focus:outline-none 
                    w-full
                    text-sm h-10
                    ${focused ? 'bg-white border-gray-300' : 'bg-gray-100 border-transparent'}
                `}
                style={{ direction: 'ltr', textAlign: 'left' }}
            />

            {showDropdown && (
                <div className="absolute top-14 w-[400px] bg-white rounded-md shadow-lg right-0 border border-t-0 z-50 max-h-[500px] overflow-y-auto">
                    <div className="space-y-2 px-4 py-2 max-h-[440px] overflow-y-auto">
                        {loading ? (
                            <div className="text-center text-sm text-gray-500 py-4">
                                Duke kërkuar...
                            </div>
                        ) : isResultsValid ? (
                            <>
                                {visibleResults.map((item, index) => {
                                    const firstVariant = item.variants?.[0];
                                    const imageUrl = process.env.REACT_APP_IMAGE_URL + firstVariant?.newImagesUrl?.[0];
                                    const cat = Array.isArray(item.categories) ? item.categories[0] : item.categories;

                                    return (
                                        <a
                                            key={item._id || index}
                                            href={`/productdetails/${item.externalId}/${cat}`}
                                            className="flex items-center space-x-4 p-2 hover:bg-gray-100 rounded-md transition"
                                            onMouseDown={(e) => {
                                                // Keep input focused so dropdown doesn't unmount before click.
                                                e.preventDefault();
                                            }}
                                        >
                                            <img
                                                src={imageUrl}
                                                alt={item.name}
                                                className="w-[60px] h-[60px] object-cover rounded-md border flex-shrink-0"
                                            />
                                            <div className="flex flex-col w-full">
                                                <p className="text-sm font-medium text-gray-900 break-words whitespace-normal">
                                                    {item.name}
                                                </p>
                                            </div>
                                        </a>
                                    );
                                })}

                                {hasMoreResults && (
                                    <button
                                        type="button"
                                        className="w-full mt-2 rounded-md border border-gray-200 bg-white py-2 text-xs font-semibold uppercase tracking-wider text-gray-700 hover:text-black hover:border-gray-300 transition-colors"
                                        onMouseDown={(e) => {
                                            // Prevent input blur from closing dropdown before navigation
                                            e.preventDefault();
                                        }}
                                        onClick={() => {
                                            navigate(`/search/${encodeURIComponent(trimmedQuery)}`);
                                        }}
                                    >
                                        Shiko më shumë
                                    </button>
                                )}
                            </>
                        ) : canShowEmptyState ? (
                            <div className="text-center text-sm text-gray-500 py-4">
                                Asnjë rezultat i gjetur.
                            </div>
                        ) : null}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchBox;
